import {
  Api,
  ApiError,
  LINKED_PRODUCTS,
  LinkError,
  type LinkedProduct,
  LinksApi,
  type SocialProvider,
  type User,
} from "@rhema/client";
import { webHasShareSheet, webShare } from "./share";
import { idbUploadSpool } from "./spool";
import type { NativeAdapters, Platform } from "./types";

/** The hosted Rhema server. QA builds can set VITE_RHEMA_SERVER, and self-hosters can change it in the app. */
export const DEFAULT_SERVER = "https://app.rhema.siliconblast.com";

export const TOKEN_KEY = "session-token";
export const SERVER_PREF = "server-origin";
export const PENDING_PREF = "pending-sign-in";
/** The product and link token of a social link waiting in the system browser. Kept in the keychain. */
export const PENDING_LINK_KEY = "pending-link";
export const pickleKeyName = (userId: string) => `pickle-key:${userId}`;

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "10.0.2.2"]);

/** Returns the origin of a server address, or null when it is not https (or http on this machine). */
export function normaliseOrigin(input: string | null | undefined): string | null {
  if (!input?.trim()) return null;
  try {
    const url = new URL(input.trim());
    const local = LOCAL_HOSTS.has(url.hostname);
    if (url.protocol !== "https:" && !(url.protocol === "http:" && local)) return null;
    return url.origin;
  } catch {
    return null;
  }
}

/**
 * The path of a rhema:// link without its slashes, such as "auth/callback" or "share". Android
 * System WebView before Chrome 130 reads rhema://share with an empty host and the path "//share",
 * while newer webviews and Safari read the host "share", so both shapes give the same answer.
 */
export function rhemaPath(url: Pick<URL, "host" | "pathname">): string {
  return `${url.host}${url.pathname}`.replace(/^\/+/, "").replace(/\/+$/, "");
}

export type Callback = { code: string; state: string } | { error: string; state: string };

/** Reads rhema://auth/callback?code=...&state=... into its parts. Anything else gives null. */
export function parseCallback(link: string): Callback | null {
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return null;
  }
  if (url.protocol !== "rhema:") return null;
  const path = rhemaPath(url);
  if (path !== "auth/callback") return null;
  const state = url.searchParams.get("state") ?? "";
  const code = url.searchParams.get("code");
  if (code && state) return { code, state };
  return { error: url.searchParams.get("error") ?? "failed", state };
}

export type LinkReturn = { linkToken: string; verified: boolean };

/**
 * Reads rhema://link/callback?link_request=...&status=..., which the web app's /link/callback page
 * opens after Google, Apple or Microsoft proves an account for a link this app started.
 */
export function parseLinkCallback(link: string): LinkReturn | null {
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return null;
  }
  if (url.protocol !== "rhema:") return null;
  const path = rhemaPath(url);
  if (path !== "link/callback") return null;
  const linkToken = url.searchParams.get("link_request") ?? "";
  if (!linkToken) return null;
  return { linkToken, verified: url.searchParams.get("status") === "verified" };
}

/** The link back into the app that the web callback page opens. */
export function linkCallbackUrl(linkToken: string, status: string): string {
  const query = new URLSearchParams({ link_request: linkToken, status });
  return `rhema://link/callback?${query.toString()}`;
}

export const LINK_NOT_CONFIRMED =
  "The other app did not confirm your account, so nothing was linked. Try again from Connected apps.";

/**
 * Reads rhema://share?text=...&url=...&title=... into the app's /share route. Malakh and Penielly
 * open Rhema this way on desktop and mobile. Anything else gives null.
 */
export function parseShareLink(link: string): string | null {
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return null;
  }
  if (url.protocol !== "rhema:") return null;
  const path = rhemaPath(url);
  if (path !== "share") return null;
  const params = new URLSearchParams();
  for (const key of ["title", "text", "url"]) {
    const value = url.searchParams.get(key);
    if (value) params.set(key, value.slice(0, 10_000));
  }
  const files = url.searchParams.getAll("file").filter(isSharedFilePath);
  for (const file of files.slice(0, MAX_SHARED_FILES)) params.append("file", file);
  const query = params.toString();
  return query ? `/share?${query}` : "/share";
}

/**
 * Reads rhema://chat/<digits>, a link that opens a chat with a phone number, into the app's
 * /chat/<digits> route. Anything else gives null.
 */
export function parseChatLink(link: string): string | null {
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return null;
  }
  if (url.protocol !== "rhema:") return null;
  const match = /^chat\/(\d{7,15})$/.exec(rhemaPath(url));
  return match ? `/chat/${match[1]}` : null;
}

/** The most files one share can bring in. */
export const MAX_SHARED_FILES = 10;

/**
 * Android copies the files another app shares into the app's cache, in a share-inbox folder named
 * by a random id, and passes their paths on rhema://share. Any app can open a rhema:// link, so
 * only paths of that shape are read, and never one that climbs out of its folder.
 */
export function isSharedFilePath(path: string): boolean {
  if (path.includes("..") || path.includes("\\")) return false;
  return /^\/[^?#]*\/share-inbox\/[0-9a-f-]{8,64}\/[^/?#]{1,255}$/.test(path);
}

/** Paths that open in the app when the system hands it an https link to its server. */
const APP_LINK_PATHS = [
  /^\/join\/[A-Za-z0-9_-]{1,128}$/,
  /^\/spaces\/join\/[A-Za-z0-9_-]{1,128}$/,
  /^\/spaces\/share\/[A-Za-z0-9_-]{1,128}$/,
  /^\/chat\/\d{7,15}$/,
];

/**
 * Reads an https link to this app's server, such as a group invite tapped in another app, into an
 * app route. iOS Universal Links and Android App Links deliver these. Anything else gives null.
 */
export function parseAppLink(link: string, origins: readonly string[]): string | null {
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || !origins.includes(url.origin)) return null;
  const path = url.pathname.replace(/\/+$/, "");
  return APP_LINK_PATHS.some((p) => p.test(path)) ? path : null;
}

function toBase64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function fromBase64(text: string): Uint8Array {
  return Uint8Array.from(atob(text), (c) => c.charCodeAt(0));
}

export const SERVER_NOT_ALLOWED =
  "This build of Rhema for desktop connects only to Rhema's servers. To use your own server, use Rhema on the web, or build the desktop app for your server as the self-hosting guide explains.";

export const SIGN_IN_FAILED = "That sign-in did not work. Try again, or use another way to sign in.";

/**
 * Opens a route that a link asked for before the app has a signed-in client, for example a share
 * that started the app. The address changes in place and the router follows it, so the usual gates
 * apply: someone signed out goes to sign in and comes back to this route afterwards. Before the
 * router exists, the router starts on this address.
 */
export function openBeforeSignIn(path: string): void {
  window.history.replaceState(window.history.state, "", path);
  window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
}

/** Reads the files a share passed on rhema://share. Paths of any other shape are skipped. */
async function readShared(a: NativeAdapters, paths: string[]): Promise<File[]> {
  const files: File[] = [];
  for (const path of paths.filter(isSharedFilePath).slice(0, MAX_SHARED_FILES)) {
    const file = await a.readSharedFile?.(path).catch(() => null);
    if (file) files.push(file);
  }
  return files;
}

type Waiting = { resolve: (user: User) => void; reject: (err: unknown) => void };
type WaitingLink = { resolve: () => void; reject: (err: unknown) => void };
type PendingLink = { product: LinkedProduct; linkToken: string };

function readPendingLink(raw: string | null): PendingLink | null {
  try {
    const value = JSON.parse(raw ?? "null") as Partial<PendingLink> | null;
    if (!value || typeof value.linkToken !== "string") return null;
    if (!LINKED_PRODUCTS.includes(value.product as LinkedProduct)) return null;
    return { product: value.product as LinkedProduct, linkToken: value.linkToken };
  } catch {
    return null;
  }
}

/**
 * Desktop and mobile. The API and socket go to the server origin with a bearer token, and the token and
 * pickle keys live in the OS keychain. Social sign-in opens the system browser and comes back on a
 * rhema://auth/callback link.
 */
export async function createNativePlatform(
  adapters: NativeAdapters,
  options: { buildServer?: string } = {},
): Promise<Platform> {
  const a = adapters;
  const defaultServerOrigin = normaliseOrigin(options.buildServer) ?? DEFAULT_SERVER;
  const origin = normaliseOrigin(await a.prefs.get(SERVER_PREF).catch(() => null)) ?? defaultServerOrigin;
  let token: string | null = await a.secrets.get(TOKEN_KEY).catch(() => null);
  const api = new Api({ baseUrl: origin, token: () => token, fetch: a.fetch });
  const listeners = new Set<(user: User) => void>();
  let waiting: Waiting | null = null;

  const saveToken = async (next: string) => {
    token = next;
    await a.secrets.set(TOKEN_KEY, next);
  };

  /** Finishes a social sign-in when its link comes back. Links this app did not start are ignored. */
  const finish = async (link: string) => {
    const callback = parseCallback(link);
    if (!callback) return;
    const expected = await a.prefs.get(PENDING_PREF).catch(() => null);
    if (!expected || callback.state !== expected) return;
    await a.prefs.set(PENDING_PREF, null);
    await a.closeBrowser?.().catch(() => undefined);
    const current = waiting;
    waiting = null;
    try {
      if ("error" in callback) throw new ApiError(400, SIGN_IN_FAILED);
      const res = await api.nativeExchange(callback.code, callback.state);
      await saveToken(res.token);
      current?.resolve(res.user);
      for (const listener of listeners) listener(res.user);
    } catch (err) {
      current?.reject(err);
    }
  };

  // Share and invite links open a route. Until a signed-in client attaches, see openBeforeSignIn.
  let navigate: ((path: string) => void) | null = null;

  /**
   * Finishes a social link when the web callback page hands the browser back. The app confirms it
   * with its own session, because SBI bound the link token to this account. Links this app did not
   * start are ignored. Returns false for anything that is not a link callback.
   */
  let linking: WaitingLink | null = null;
  const finishLink = async (link: string): Promise<boolean> => {
    const back = parseLinkCallback(link);
    if (!back) return false;
    const pending = readPendingLink(await a.secrets.get(PENDING_LINK_KEY).catch(() => null));
    if (!pending || pending.linkToken !== back.linkToken) return true;
    await a.secrets.delete(PENDING_LINK_KEY).catch(() => undefined);
    await a.closeBrowser?.().catch(() => undefined);
    const current = linking;
    linking = null;
    try {
      if (!back.verified) throw new LinkError("failed", 400, pending.product, LINK_NOT_CONFIRMED);
      await new LinksApi(api).confirm(pending.product, pending.linkToken);
      current?.resolve();
      // The app was restarted while the browser was open, so no screen is waiting.
      if (!current) navigate?.("/you#connected-apps");
    } catch (err) {
      current?.reject(err);
    }
    return true;
  };
  const appOrigins = [origin, defaultServerOrigin];
  const route = (link: string) => {
    const path = parseShareLink(link) ?? parseChatLink(link) ?? parseAppLink(link, appOrigins);
    if (!path) return false;
    if (navigate) navigate(path);
    else openBeforeSignIn(path);
    return true;
  };

  const handle = async (link: string) => {
    if (route(link)) return;
    if (await finishLink(link)) return;
    await finish(link);
  };
  await a.onDeepLink((link) => void handle(link));
  // The OS may have started the app to open the link, for example after it was closed during sign-in.
  for (const link of await a.launchUrls().catch(() => [])) await handle(link);
  await a.setup?.();

  return {
    kind: a.kind,
    native: true,
    device: a.device,
    authClient: "native",
    api,
    deviceName: () => a.deviceName,
    serverOrigin: () => origin,
    defaultServerOrigin,
    setServerOrigin: async (next) => {
      const value = next === null ? null : normaliseOrigin(next);
      if (next !== null && !value) throw new Error("Enter a server address that starts with https://");
      if (value && a.allowsServer && !(await a.allowsServer(value))) throw new Error(SERVER_NOT_ALLOWED);
      await a.prefs.set(SERVER_PREF, value === defaultServerOrigin ? null : value);
      // A token only works on the server that issued it.
      token = null;
      await a.secrets.delete(TOKEN_KEY);
      window.location.assign("/sign-in");
    },
    clientOptions: (userId) => ({
      uploadSpool: a.uploadSpool?.(userId) ?? idbUploadSpool(userId),
      socketUrl: origin,
      socketToken: () => token,
      pickleKey: async () => {
        const name = pickleKeyName(userId);
        const stored = await a.secrets.get(name);
        if (stored) return fromBase64(stored);
        const key = crypto.getRandomValues(new Uint8Array(32));
        await a.secrets.set(name, toBase64(key));
        return key;
      },
    }),
    saveToken,
    signInWith: async (idp) => {
      const { url, state } = await api.startNative(idp);
      await a.prefs.set(PENDING_PREF, state);
      const done = new Promise<User>((resolve, reject) => {
        waiting = { resolve, reject };
      });
      await a.openUrl(url);
      return done;
    },
    linkSocial: async (product: LinkedProduct, linkToken: string, idp: SocialProvider) => {
      const url = await new LinksApi(api).nativeSocialUrl(product, linkToken, idp);
      await a.secrets.set(PENDING_LINK_KEY, JSON.stringify({ product, linkToken } satisfies PendingLink));
      linking?.reject(new LinkError("failed", 409, product, "Linking started again."));
      const done = new Promise<void>((resolve, reject) => {
        linking = { resolve, reject };
      });
      await a.openUrl(url);
      return done;
    },
    openExternal: (url) => a.openUrl(url),
    ...(a.readSharedFile ? { sharedFiles: (paths: string[]) => readShared(a, paths) } : {}),
    ...(a.saveFile ? { saveFile: a.saveFile } : {}),
    ...(a.pickPhotos ? { pickPhotos: a.pickPhotos } : {}),
    ...(a.setInCall ? { setInCall: a.setInCall } : {}),
    share: a.share ?? webShare,
    hasShareSheet: a.share ? (a.hasShareSheet ?? (() => true)) : webHasShareSheet,
    ...(a.readAddressBook ? { readAddressBook: a.readAddressBook } : {}),
    ...(a.scanCode ? { scanCode: a.scanCode } : {}),
    onSignedIn: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    existingPickleKey: async (userId) => {
      const stored = await a.secrets.get(pickleKeyName(userId)).catch(() => null);
      return stored ? fromBase64(stored) : null;
    },
    signOut: async (userId, options = {}) => {
      await a.beforeSignOut?.(api).catch(() => undefined);
      try {
        await api.logout();
      } catch {
        // Already signed out on the server. Clear this device anyway.
      }
      token = null;
      await a.secrets.delete(TOKEN_KEY).catch(() => undefined);
      // A pickle key is only ever removed when the person chose to, after the backup check.
      if (!options.keep)
        for (const id of [userId, ...(options.also ?? [])])
          if (id) await a.secrets.delete(pickleKeyName(id)).catch(() => undefined);
      return "/sign-in";
    },
    attach: (client, context) => {
      navigate = context.navigate;
      const stop = a.attach?.(client, api, context);
      return () => {
        navigate = null;
        stop?.();
      };
    },
  };
}
